import { useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import WaveSurfer from "wavesurfer.js";
import { formatAudioTime } from "@utils/media/audioTime";
import {
  IconArrowBackUp,
  IconArrowsJoin,
  IconArrowsLeftRight,
  IconArrowLeft,
  IconArrowRampRight,
  IconArrowRight,
  IconArrowUp,
  IconGitBranch,
  IconMapPin,
  IconParking,
  IconRoad,
  IconRotateClockwise2,
} from "@tabler/icons-react";
import { IconPlayerPause, IconPlayerPlay } from "@tabler/icons-react";
import { resolveMediaKind } from "@utils/media/mediaUrl";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { getTurnTypeDefinitionByValue } from "@utils/mode_tool/turnType";

function readLocalizedValue(entry) {
  if (typeof entry === "string") return entry;
  if (entry && typeof entry === "object") return entry.value || "";
  return "";
}

function getLocalizedEntries(field) {
  if (!field || typeof field !== "object") return [];
  if (Array.isArray(field)) {
    return field.reduce((acc, item) => {
      if (!item || typeof item !== "object") return acc;
      const entries = Object.entries(item).filter(([key]) => key !== "id");
      if (!entries.length) return acc;
      const [iso, value] = entries[0];
      if (!iso) return acc;
      const normalizedValue = readLocalizedValue(value);
      if (!normalizedValue) return acc;
      acc.push({
        iso,
        value: normalizedValue,
      });
      return acc;
    }, []);
  }
  return Object.entries(field)
    .filter(([iso]) => Boolean(iso))
    .map(([iso, entry]) => ({
      iso,
      value: readLocalizedValue(entry),
    }))
    .filter((item) => item.value);
}

function pickPreferredLanguageIso(meta = {}) {
  const requestedIso = String(meta?.activeLanguageIso || "")
    .trim()
    .toLowerCase();
  const localizedCandidates = [
    ...getLocalizedEntries(meta?.name || {}),
    ...getLocalizedEntries(meta?.description || {}),
  ];
  const localizedIsos = localizedCandidates
    .map((item) => String(item?.iso || "").toLowerCase())
    .filter(Boolean);
  const audioIsos = (Array.isArray(meta?.multimedia) ? meta.multimedia : [])
    .map((item) =>
      String(item?.language || item?.languageIso || "").toLowerCase(),
    )
    .filter(Boolean);
  const availableIsos = [...new Set([...localizedIsos, ...audioIsos])];
  if (requestedIso && availableIsos.includes(requestedIso)) return requestedIso;
  return availableIsos[0] || requestedIso || "";
}

function pickFirstAvailableLanguageIso(items = [], preferredLanguageIso = "") {
  const availableIsos = (Array.isArray(items) ? items : [])
    .map((item) =>
      String(item?.language || item?.languageIso || "").toLowerCase(),
    )
    .filter(Boolean);
  if (preferredLanguageIso && availableIsos.includes(preferredLanguageIso)) {
    return preferredLanguageIso;
  }
  return availableIsos[0] || "";
}

function stripHtml(html) {
  return String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function toExcerpt(text, maxLength = 120) {
  const normalized = String(text || "").trim();
  if (!normalized) return "";
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength).trim()}...`;
}

function AudioWavePreview({ url = "" }) {
  const hostRef = useRef(null);
  const waveRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (!hostRef.current || !url) return;
    setCurrentTime(0);
    setDuration(0);
    const wave = WaveSurfer.create({
      container: hostRef.current,
      url,
      fetchParams: {
        credentials: "include",
      },
      height: 30,
      barWidth: 2,
      barGap: 1,
      cursorWidth: 1,
      waveColor: "#94a3b8",
      progressColor: "#0ea5e9",
      cursorColor: "#f8fafc",
      normalize: true,
    });
    waveRef.current = wave;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onReady = (audioDuration) => setDuration(audioDuration || 0);
    const onTimeUpdate = (time) => setCurrentTime(time || 0);
    const onFinish = () => {
      setIsPlaying(false);
      setCurrentTime(wave.getDuration() || 0);
    };
    wave.on("play", onPlay);
    wave.on("pause", onPause);
    wave.on("ready", onReady);
    wave.on("timeupdate", onTimeUpdate);
    wave.on("finish", onFinish);
    return () => {
      wave.un("play", onPlay);
      wave.un("pause", onPause);
      wave.un("ready", onReady);
      wave.un("timeupdate", onTimeUpdate);
      wave.un("finish", onFinish);
      wave.destroy();
      waveRef.current = null;
    };
  }, [url]);

  return (
    <div className="mode-point-popup-wave-preview">
      <button
        type="button"
        className="app-icon-btn app-icon-btn--update mode-point-popup-wave-play"
        onClick={() => waveRef.current?.playPause()}
        aria-label={isPlaying ? "Pause audio preview" : "Play audio preview"}
        title={isPlaying ? "Pause" : "Play"}
      >
        {isPlaying ? (
          <IconPlayerPause size={14} />
        ) : (
          <IconPlayerPlay size={14} />
        )}
      </button>
      <div ref={hostRef} className="mode-point-popup-wave-host" />
      <span className="mode-point-popup-wave-time">
        {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
      </span>
    </div>
  );
}

const TURN_TYPE_ICON_BY_KEY = {
  point: IconMapPin,
  continueStraight: IconArrowUp,
  rightTurn: IconArrowRight,
  leftTurn: IconArrowLeft,
  uTurn: IconArrowBackUp,
  roundaboutTurn: IconRotateClockwise2,
  mergeTurn: IconArrowsJoin,
  slipRoadTurn: IconGitBranch,
  laneChangeFromRight: IconRoad,
  laneChangeFromLeft: IconRoad,
  offsetTurn: IconArrowRampRight,
  dividedHighwayCrossover: IconArrowsLeftRight,
  parking: IconParking,
};

function ModePointPopupCard({ point, meta, onClose }) {
  const media = Array.isArray(meta?.multimedia) ? meta.multimedia : [];
  const preferredLanguageIso = pickPreferredLanguageIso(meta);
  const imageItems = media.filter(
    (item) => resolveMediaKind(item) === "image" && item?.url,
  );
  const availableAudioItems = media.filter(
    (item) => resolveMediaKind(item) === "audio" && item?.url,
  );
  const audioLanguageIso = pickFirstAvailableLanguageIso(
    availableAudioItems,
    preferredLanguageIso,
  );
  const audioItems = availableAudioItems.filter((item) => {
    const itemLanguage = String(
      item?.language || item?.languageIso || "",
    ).toLowerCase();
    if (!audioLanguageIso) return !itemLanguage;
    return itemLanguage === audioLanguageIso;
  });
  const resolvedLanguageIso =
    preferredLanguageIso ||
    audioLanguageIso ||
    getLocalizedEntries(meta?.name || {})[0]?.iso ||
    getLocalizedEntries(meta?.description || {})[0]?.iso ||
    "";
  const nameEntries = getLocalizedEntries(meta?.name || {}).filter((item) =>
    resolvedLanguageIso
      ? String(item?.iso || "").toLowerCase() === resolvedLanguageIso
      : true,
  );
  const descriptionEntries = getLocalizedEntries(meta?.description || {})
    .filter((item) =>
      resolvedLanguageIso
        ? String(item?.iso || "").toLowerCase() === resolvedLanguageIso
        : true,
    )
    .map((item) => ({
      iso: item.iso,
      value: toExcerpt(stripHtml(item.value), 120),
    }));
  const moreInfoEntries = getLocalizedEntries(meta?.more_info || {})
    .filter((item) =>
      resolvedLanguageIso
        ? String(item?.iso || "").toLowerCase() === resolvedLanguageIso
        : true,
    )
    .map((item) => ({
      iso: item.iso,
      value: toExcerpt(stripHtml(item.value), 120),
    }));
  const notesValue = String(meta?.notes ?? meta?.note ?? "").trim();
  const aiBriefValue = String(meta?.ai_brief ?? meta?.aiBrief ?? "").trim();

  const latValue = meta?.latitude ?? point?.lat ?? "";
  const lngValue = meta?.longitude ?? point?.lng ?? "";
  const radiusMeters = Number(meta?.radiusMeters ?? 0);
  const turnCase = Number(meta?.turnCase ?? 0);

  const showLat = String(latValue || "").trim() !== "";
  const showLng = String(lngValue || "").trim() !== "";
  const showRadius = Number.isFinite(radiusMeters) && radiusMeters > 0;
  const showTurnCase = Number.isFinite(turnCase) && turnCase > 0;
  const turnTypeDefinition = getTurnTypeDefinitionByValue(turnCase);
  const TurnTypeIcon =
    TURN_TYPE_ICON_BY_KEY[turnTypeDefinition?.key] || IconMapPin;

  const sliderSettings = {
    dots: imageItems.length > 1,
    arrows: imageItems.length > 1,
    infinite: imageItems.length > 1,
    speed: 300,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  return (
    <div className="mode-point-popup">
      <div className="mode-point-popup-form">
        <div className="mode-point-popup-head">
          <p className="mode-point-popup-title">
            {nameEntries.length ? (
              <div className="mode-point-popup-card">
                {nameEntries.map((item) => (
                  <p className="mode-point-popup-desc" key={`name-${item.iso}`}>
                    {showTurnCase ? <TurnTypeIcon size={14} /> : null}
                    {item.value}
                  </p>
                ))}
              </div>
            ) : (
              <>Point #{point?.order || "-"}</>
            )}
          </p>
          <button
            className="app-icon-btn app-icon-btn--back"
            type="button"
            aria-label="Close preview"
            title="Close preview"
            onClick={onClose}
          >
            x
          </button>
        </div>

        <div className="mode-point-popup-card">
          {imageItems.length ? (
            <div className="mode-point-popup-slider">
              <Slider {...sliderSettings}>
                {imageItems.map((item) => (
                  <div key={String(item?.id || item?.url)}>
                    <img
                      src={String(item?.url || "")}
                      alt="Point media"
                      className="mode-point-popup-image"
                    />
                  </div>
                ))}
              </Slider>
            </div>
          ) : (
            <p className="mode-point-popup-empty">No images</p>
          )}

          {audioItems.length ? (
            <div className="mode-point-popup-audio-list">
              {audioItems.map((item, index) => (
                <div
                  className="mode-point-popup-audio-item"
                  key={String(item?.id || item?.url || index)}
                >
                  <small>Audio {index + 1}</small>
                  <AudioWavePreview url={String(item?.url || "")} />
                </div>
              ))}
            </div>
          ) : null}

          {notesValue ? (
            <div className="mode-point-popup-copy-block">
              <strong>Notes</strong>
              <p className="mode-point-popup-desc">{notesValue}</p>
            </div>
          ) : null}
          {aiBriefValue ? (
            <div className="mode-point-popup-copy-block">
              <strong>AI brief</strong>
              <p className="mode-point-popup-desc">{aiBriefValue}</p>
            </div>
          ) : null}
        </div>

        {/*   {nameEntries.length ? (
          <div className="mode-point-popup-card">
            <p className="mode-point-popup-card-title">Name</p>
            {nameEntries.map((item) => (
              <p className="mode-point-popup-desc" key={`name-${item.iso}`}>
                {item.value}
              </p>
            ))}
          </div>
        ) : null} */}

        {descriptionEntries.length ? (
          <div className="mode-point-popup-card">
            <p className="mode-point-popup-card-title">Description</p>
            {descriptionEntries.map((item) => (
              <p className="mode-point-popup-desc" key={`desc-${item.iso}`}>
                {item.value}
              </p>
            ))}
          </div>
        ) : null}

        {showLat || showLng || showRadius || showTurnCase ? (
          <div className="mode-point-popup-card">
            {/*             <p className="mode-point-popup-card-title">Details</p>
             */}{" "}
            {moreInfoEntries.length ? (
              <div className="mode-point-popup-card">
                <p className="mode-point-popup-card-title">More Info</p>
                {moreInfoEntries.map((item) => (
                  <p className="mode-point-popup-desc" key={`more-${item.iso}`}>
                    {item.value}
                  </p>
                ))}
              </div>
            ) : null}
            {/* {showLat ? (
              <p className="mode-point-popup-coord">Lat: {String(latValue)}</p>
            ) : null}
            {showLng ? (
              <p className="mode-point-popup-coord">Lng: {String(lngValue)}</p>
            ) : null} */}
            <p className="mode-point-popup-card-title">Radius:</p>
            {showRadius ? (
              <p className="mode-point-popup-coord"> {radiusMeters} m</p>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default ModePointPopupCard;
